// "Why did my target change?" — the adaptive engine's working, in plain
// language (F2). MacroFactor's most-praised behavior is explaining itself;
// all the data already lives in the goal_adjustments row. The mascot's
// `explaining` pose slots in here once the character lands (U2).
import { useT } from "../lib/i18n";
import { renderAdjustReason } from "../lib/adjustReason";
import { SheetShell } from "./SheetShell";
import { Mascot } from "./Mascot";
import type { GoalAdjustment } from "@shared/types";

interface Props {
  adj: GoalAdjustment;
  onClose: () => void;
}

export function TargetExplainerSheet({ adj, onClose }: Props) {
  const t = useT();
  const days = Math.round(
    (Date.parse(adj.window_end) - Date.parse(adj.window_start)) / 86400_000,
  ) + 1;
  const delta = adj.daily_calories_after - adj.daily_calories_before;
  const deltaStr = `${delta > 0 ? "+" : ""}${delta}`;

  return (
    <SheetShell onClose={onClose}>
      <div className="flex items-end gap-3 mt-4 mb-1">
        <Mascot pose="explaining" size={48} />
        <h3 className="text-lg font-semibold">{t("adjust.explain_title")}</h3>
      </div>
      <p className="text-sm text-fg-muted mb-4">
        {t("adjust.explain_intro", { days: String(days) })}
      </p>

      <div className="space-y-3 mb-4">
        <div className="bg-surface rounded-2xl p-4">
          <div className="text-sm text-fg-muted uppercase tracking-wider mb-1">
            {t("adjust.explain_burn_label")}
          </div>
          <div className="text-2xl font-semibold tabular-nums">
            {adj.measured_tdee_kcal} kcal
          </div>
          <p className="text-sm text-fg-muted mt-1">{t("adjust.explain_burn")}</p>
        </div>

        <div className="bg-surface rounded-2xl p-4">
          <div className="text-sm text-fg-muted uppercase tracking-wider mb-1">
            {t("adjust.explain_target_label")}
          </div>
          <div className="text-2xl font-semibold tabular-nums">
            {adj.daily_calories_before} → {adj.daily_calories_after}
            <span className="text-sm text-fg-muted ml-2">({deltaStr} kcal)</span>
          </div>
          {renderAdjustReason(adj.reason, t) && (
            <p className="text-sm text-fg-muted mt-1">{renderAdjustReason(adj.reason, t)}</p>
          )}
        </div>
      </div>

      <p className="text-sm text-fg-secondary leading-relaxed mb-4">
        {t("adjust.explain_how")}
      </p>
      <p className="text-sm text-fg-muted leading-relaxed mb-4">
        {t("adjust.explain_safety")}
      </p>
    </SheetShell>
  );
}
